import React from 'react';
import { TrendingUp, TrendingDown, Minus, Clock } from 'lucide-react';
import {
  STATUS_META,
  formatInr,
  formatSignedInr,
  formatSignedPercent,
} from '../../../utils/gmpBoard';
import CompanyLogo from '../../common/CompanyLogo';

// Status chips tuned for the dark hero background
const HERO_STATUS_TONES = {
  open: 'bg-emerald-400/15 text-emerald-200 ring-emerald-300/30',
  closing_today: 'bg-rose-400/20 text-rose-100 ring-rose-300/40',
  closed: 'bg-white/10 text-slate-200 ring-white/20',
  allotted: 'bg-amber-400/15 text-amber-200 ring-amber-300/30',
  listed: 'bg-violet-400/15 text-violet-200 ring-violet-300/30',
  upcoming: 'bg-sky-400/15 text-sky-200 ring-sky-300/30',
  other: 'bg-white/10 text-slate-200 ring-white/20',
};

export const CompanyGmpHero = ({ row, logoUrl }) => {
  const status = STATUS_META[row.status] || STATUS_META.other;
  const statusLabel = row.status === 'other' ? row.statusLabel || status.label : status.label;
  const TrendIcon = !row.hasGmp ? null : row.gmp > 0 ? TrendingUp : row.gmp < 0 ? TrendingDown : Minus;
  const gmpTone = !row.hasGmp || row.gmp === 0 ? 'text-white' : row.gmp > 0 ? 'text-emerald-300' : 'text-rose-300';

  let listingNote = 'Upper price band + latest GMP';
  if (row.price === null) listingNote = 'Price band not announced yet';
  else if (!row.hasGmp) listingNote = 'Needs a grey market quote';

  return (
    <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#012f2f] via-[#014848] to-[#006d6d] text-white shadow-elevated">
      <div aria-hidden="true" className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-teal-300/10 blur-2xl" />
      <div aria-hidden="true" className="absolute -bottom-32 left-1/3 w-96 h-96 rounded-full bg-emerald-300/10 blur-3xl" />

      <div className="relative p-5 sm:p-8 flex flex-col xl:flex-row xl:items-center justify-between gap-6 xl:gap-10">
        {/* Identity */}
        <div className="flex items-start gap-4 min-w-0">
          <CompanyLogo
            company={row.name}
            logoUrl={logoUrl}
            size="xl"
            fallbackClassName="bg-white/10 ring-1 ring-white/20 tracking-tight"
          />
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-200/90">
              Grey Market Premium
            </p>
            <h1 className="mt-1 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight break-words">
              {row.name} IPO GMP
            </h1>

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/10 ring-1 ring-white/20">
                {row.board === 'SME' ? 'SME IPO' : 'Mainboard IPO'}
              </span>
              {row.exchange && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-white/10 ring-1 ring-white/20">
                  {row.exchange}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ring-1 ${
                  HERO_STATUS_TONES[row.status] || HERO_STATUS_TONES.other
                }`}
              >
                {(row.status === 'open' || row.status === 'closing_today') && (
                  <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                )}
                {statusLabel}
              </span>
              <span className="text-[13px] tracking-[1px]" aria-label={`Fire rating ${row.fireRating} out of 5`}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span key={i} className={i < row.fireRating ? 'text-amber-300' : 'text-white/20'}>
                    ★
                  </span>
                ))}
              </span>
            </div>

            {row.updatedOn && (
              <p className="flex items-center gap-1.5 mt-3 text-xs text-teal-100/80">
                <Clock className="w-3.5 h-3.5" />
                Source updated {row.updatedOn}
              </p>
            )}
          </div>
        </div>

        {/* Headline numbers */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full xl:w-auto xl:min-w-[420px]">
          <div className="rounded-2xl bg-white/10 ring-1 ring-white/15 p-4 sm:p-5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-teal-100/90">Latest GMP</div>
            <div className={`flex items-center gap-2 mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight ${gmpTone}`}>
              {TrendIcon && <TrendIcon className="w-6 h-6 flex-shrink-0" />}
              {row.hasGmp ? formatSignedInr(row.gmp) : '—'}
            </div>
            <div className="mt-2 text-xs text-teal-100/90">
              {row.hasGmp && row.gmpPercent !== null ? (
                <span className="inline-flex px-2 py-0.5 rounded-md bg-white/10 font-bold">
                  {formatSignedPercent(row.gmpPercent)} per share
                </span>
              ) : (
                'Not quoted in the grey market yet'
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-white text-slate-900 p-4 sm:p-5 shadow-lg">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Est. Listing Price</div>
            <div className="mt-1 text-3xl sm:text-4xl font-extrabold tracking-tight text-emerald-600">
              {row.estListing !== null ? formatInr(row.estListing) : '—'}
            </div>
            <div className="mt-2 text-xs text-slate-500">{listingNote}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyGmpHero;
